import React, { useEffect, useState } from 'react';
import { confirmAction } from '../utils/confirm';
import { downloadZigbeeDb, uploadZigbeeDb } from '../services/gatewaysApi';
import { fetchGateways, addGateway, updateGateway, deleteGateway, GatewayRecord, API_ORIGIN } from '../services/api';
import ErrorBanner from './shared/ErrorBanner';
import OtaSection from './OtaSection';
import ApiKeysSection from './gateways/ApiKeysSection';
import DeviationSettings from './gateways/DeviationSettings';
import OfflineSettings from './gateways/OfflineSettings';
import BatteryTypesPanel from './settings/BatteryTypesPanel';
import PageHeader from './shared/PageHeader';
import ZigbeeDefaultSettingsPanel from './settings/ZigbeeDefaultSettingsPanel';
import { useAuth } from '../context/AuthContext';
import { formatTimeInTZ } from '../utils/timezone';

interface ZigbeeFormFields {
    zigbeeEnabled: boolean;
    zigbeeIp: string;
    zigbeePort: string;
    zigbeeChannel: string;
    zigbeePanId: string;
    zigbeeExtendedPanId: string;
    zigbeeNetworkKey: string;
}

function randomHex(bytes: number): string {
    const arr = new Uint8Array(bytes);
    crypto.getRandomValues(arr);
    return Array.from(arr).map(b => b.toString(16).padStart(2, '0')).join('');
}

function randomPanId(): string {
    return String(Math.floor(Math.random() * 0xfffe) + 1);
}

function randomChannel(): number {
    // Prefer the Zigbee channels that sit in the gaps between the common WiFi channels (1/6/11),
    // which minimises 2.4 GHz interference: 15 (between WiFi 1 & 6), 20 (between 6 & 11),
    // and 25/26 (above WiFi 11). Avoids the consumer defaults (11/15 on IKEA/HA) overlap too.
    const WIFI_FRIENDLY = [15, 20, 25, 26];
    return WIFI_FRIENDLY[Math.floor(Math.random() * WIFI_FRIENDLY.length)];
}

function createEmptyZigbee(): ZigbeeFormFields {
    return {
        zigbeeEnabled: true,
        zigbeeIp: '',
        zigbeePort: '6638',
        zigbeeChannel: String(randomChannel()),
        zigbeePanId: randomPanId(),
        zigbeeExtendedPanId: randomHex(8),
        zigbeeNetworkKey: randomHex(16),
    };
}

function randomGatewayId(): string {
    return 'gw-' + randomHex(4);
}

function createEmptyForm() {
    return { id: randomGatewayId(), name: '', ip: '', description: '', scriptUrl: '', ...createEmptyZigbee() };
}

function zigbeeFromGw(gw: GatewayRecord): ZigbeeFormFields {
    return {
        zigbeeEnabled: gw.zigbeeEnabled ?? false,
        zigbeeIp: gw.zigbeeIp ?? gw.ip,
        zigbeePort: String(gw.zigbeePort ?? 6638),
        zigbeeChannel: String(gw.zigbeeChannel ?? 15),
        zigbeePanId: String(gw.zigbeePanId ?? 6754),
        // If the gateway has no unique identity in DB, generate one so it gets saved on next edit.
        zigbeeExtendedPanId: gw.zigbeeExtendedPanId ?? randomHex(8),
        zigbeeNetworkKey: gw.zigbeeNetworkKey ?? randomHex(16),
    };
}

const Gateways: React.FC = () => {
    const { timezone } = useAuth();
    const [gateways, setGateways] = useState<GatewayRecord[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showAddForm, setShowAddForm] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [form, setForm] = useState(createEmptyForm);
    const [editForm, setEditForm] = useState(() => ({ name: '', ip: '', description: '', scriptUrl: '', ...createEmptyZigbee() }));
    const [showZigbeeAdd, setShowZigbeeAdd] = useState(false);
    const [showZigbeeEdit, setShowZigbeeEdit] = useState(false);
    const [saving, setSaving] = useState(false);
    const [uploadingDb, setUploadingDb] = useState<Set<string>>(new Set());

    const load = async () => {
        setLoading(true);
        try {
            const data = await fetchGateways();
            setGateways(data);
            setError(null);
        } catch (e: any) {
            setError(e.message || 'Kunde inte hämta gateways');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const buildZigbeePayload = (f: ZigbeeFormFields) => f.zigbeeEnabled ? {
        zigbeeEnabled: true,
        zigbeeIp: f.zigbeeIp || undefined,
        zigbeePort: f.zigbeePort ? Number(f.zigbeePort) : undefined,
        zigbeeChannel: f.zigbeeChannel ? Number(f.zigbeeChannel) : undefined,
        zigbeePanId: f.zigbeePanId ? Number(f.zigbeePanId) : undefined,
        zigbeeExtendedPanId: f.zigbeeExtendedPanId || undefined,
        zigbeeNetworkKey: f.zigbeeNetworkKey || undefined,
    } : { zigbeeEnabled: false };

    const handleAdd = async () => {
        if (!form.name.trim() || !form.ip.trim()) {
            setError('Namn och IP måste fyllas i'); return;
        }
        setSaving(true);
        try {
            await addGateway({ id: form.id, name: form.name, ip: form.ip, description: form.description, scriptUrl: (form as any).scriptUrl || undefined, ...buildZigbeePayload(form) });
            setForm(createEmptyForm()); setShowAddForm(false); setShowZigbeeAdd(false);
            await load();
        } catch (e: any) {
            setError(e.response?.data?.message || e.message || 'Kunde inte lägga till gatewayen');
        } finally { setSaving(false); }
    };

    const handleEditSave = async (id: string) => {
        if (!editForm.name.trim() || !editForm.ip.trim()) {
            setError('Namn och IP måste fyllas i'); return;
        }
        setSaving(true);
        try {
            await updateGateway(id, { name: editForm.name, ip: editForm.ip, description: editForm.description, scriptUrl: editForm.scriptUrl || undefined, ...buildZigbeePayload(editForm) });
            setEditingId(null); setShowZigbeeEdit(false);
            await load();
        } catch (e: any) {
            setError(e.response?.data?.message || e.message || 'Kunde inte spara gatewayen');
        } finally { setSaving(false); }
    };

    const handleDelete = async (id: string, name: string) => {
        if (!confirmAction(`Ta bort gatewayen "${name}"? Bara registreringen tas bort — själva enheten påverkas inte.`)) return;
        try {
            await deleteGateway(id);
            await load();
        } catch (e: any) {
            setError(e.response?.data?.message || e.message || 'Kunde inte ta bort gatewayen');
        }
    };

    const startEdit = (gw: GatewayRecord) => {
        setEditingId(gw.id);
        setEditForm({ name: gw.name, ip: gw.ip, description: gw.description || '', scriptUrl: gw.scriptUrl || '', ...zigbeeFromGw(gw) });
        setShowZigbeeEdit(gw.zigbeeEnabled ?? false);
    };

    const inputStyle: React.CSSProperties = {
        padding: '7px 10px', fontSize: '13px', border: '1px solid #ccc',
        borderRadius: '4px', width: '100%', boxSizing: 'border-box',
    };
    const btnStyle = (color: string, bg: string, border: string): React.CSSProperties => ({
        padding: '6px 14px', fontSize: '13px', cursor: 'pointer',
        backgroundColor: bg, color, border: `1px solid ${border}`, borderRadius: '4px', fontWeight: 'bold',
    });

    const statusDot = (gw: GatewayRecord) => {
        const rep = (gw as any).report;
        if (!rep) return <span style={{ color: '#aaa', fontSize: '12px' }}>⏳ ingen rapport</span>;
        const online = rep.devices?.filter((d: any) => d.online).length ?? 0;
        const total  = rep.devices?.length ?? 0;
        const age    = rep.reportedAt ? formatTimeInTZ(rep.reportedAt, timezone) : '—';
        return (
            <span style={{ fontSize: '12px', color: '#555' }}>
                <span style={{ color: online === total ? '#28a745' : '#ffa000', fontWeight: 'bold' }}>
                    ● {online}/{total} uppkopplade
                </span>
                &nbsp;· senaste rapport {age}
            </span>
        );
    };

    const coordinatorBadge = (gw: GatewayRecord) => {
        if (!gw.zigbeeEnabled) return <span style={{ fontSize: '11px', color: '#aaa' }}>avstängd</span>;
        const cs = gw.coordinatorStatus;
        const connected = cs?.connected;
        const state = cs?.state ?? 'okänt';
        return (
            <span style={{
                display: 'inline-block', padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: 'bold',
                background: connected ? '#d4edda' : '#f8d7da', color: connected ? '#155724' : '#721c24',
                border: `1px solid ${connected ? '#28a745' : '#dc3545'}`,
            }}>
                {connected ? '● Uppkopplad' : `○ ${state}`}
            </span>
        );
    };

    const zigbeeSection = (
        fields: ZigbeeFormFields,
        setFields: (fn: (f: any) => any) => void,
        show: boolean,
        setShow: (v: boolean) => void,
        ipFallback: string
    ) => (
        <div style={{ marginTop: '12px', border: '1px solid #dee2e6', borderRadius: '4px', overflow: 'hidden' }}>
            <button type="button" onClick={() => setShow(!show)}
                style={{ width: '100%', background: '#e8f0fe', border: 'none', padding: '8px 12px', cursor: 'pointer', textAlign: 'left', fontWeight: 'bold', fontSize: '13px', display: 'flex', justifyContent: 'space-between' }}>
                <span>📡 Inställningar för Zigbee-samordnare</span>
                <span>{show ? '▲' : '▼'}</span>
            </button>
            {show && (
                <div style={{ padding: '12px', background: '#f8f9ff' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', fontSize: '13px', fontWeight: 'bold' }}>
                        <input type="checkbox" checked={fields.zigbeeEnabled}
                            onChange={e => setFields((f: any) => ({ ...f, zigbeeEnabled: e.target.checked }))} />
                        Använd den här gatewayen som Zigbee-samordnare
                    </label>
                    {fields.zigbeeEnabled && (
                        <div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px' }}>
                                {[
                                    { label: 'Samordnarens IP', key: 'zigbeeIp', placeholder: ipFallback || '192.168.5.158' },
                                    { label: 'Port', key: 'zigbeePort', placeholder: '6638' },
                                    { label: 'Kanal (11–26)', key: 'zigbeeChannel', placeholder: '20' },
                                    { label: 'PAN ID', key: 'zigbeePanId', placeholder: '6754' },
                                    { label: 'Utökat PAN-ID (hex)', key: 'zigbeeExtendedPanId', placeholder: 'dddddddddddddddd' },
                                    { label: 'Nätverksnyckel (hex)', key: 'zigbeeNetworkKey', placeholder: '01030507090b0d0f00020406080a0c0d' },
                                ].map(({ label, key, placeholder }) => (
                                    <div key={key}>
                                        <label style={{ fontSize: '12px', fontWeight: 'bold', display: 'block', marginBottom: '4px' }}>{label}</label>
                                        <input style={inputStyle} placeholder={placeholder}
                                            value={(fields as any)[key]}
                                            onChange={e => setFields((f: any) => ({ ...f, [key]: e.target.value }))} />
                                    </div>
                                ))}
                            </div>
                            <button
                                type="button"
                                title="Slumpar fram ny kanal (20–26), utökat PAN-ID och nätverksnyckel. Spara för att det ska gälla."
                                onClick={() => setFields((f: any) => ({
                                    ...f,
                                    zigbeeChannel: String(randomChannel()),
                                    zigbeePanId: randomPanId(),
                                    zigbeeExtendedPanId: randomHex(8),
                                    zigbeeNetworkKey: randomHex(16),
                                }))}
                                style={{ marginTop: '10px', fontSize: '12px', padding: '5px 12px', background: '#e8f4fd', border: '1px solid #90caf9', borderRadius: '4px', cursor: 'pointer', color: '#1565c0' }}
                            >
                                🎲 Slumpa fram nya värden (kanal 20–26)
                            </button>
                            <span style={{ marginLeft: '8px', fontSize: '11px', color: '#888' }}>Använd vid ny installation så att kanalerna inte krockar</span>
                        </div>
                    )}
                    {!fields.zigbeeEnabled && (
                        <p style={{ margin: 0, fontSize: '12px', color: '#888' }}>
                            Kryssa i rutan ovanför för att använda gatewayen som Zigbee-samordnare.
                        </p>
                    )}
                </div>
            )}
        </div>
    );

    return (
        <div>
            <PageHeader icon="📡" title="Gateways" description="Samordnare och gateways ute hos kunden, med Zigbee-inställningar, batterityper och API-nycklar." />

            {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}

            <BatteryTypesPanel />

            <ZigbeeDefaultSettingsPanel />

            <OfflineSettings onError={setError} />

            <DeviationSettings onError={setError} />

            {/* Toolbar */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h2 style={{ margin: 0 }}>Registrerade gateways ({gateways.length})</h2>
                <button onClick={() => { if (!showAddForm) { setForm(createEmptyForm()); setShowZigbeeAdd(true); } setShowAddForm(!showAddForm); setError(null); }}
                    style={btnStyle('#155724', '#d4edda', '#28a745')}>
                    {showAddForm ? 'Avbryt' : '+ Lägg till gateway'}
                </button>
            </div>

            {/* Add Form */}
            {showAddForm && (
                <div style={{ background: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '6px', padding: '16px', marginBottom: '20px', maxWidth: '640px' }}>
                    <h3 style={{ margin: '0 0 12px' }}>Ny gateway</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px', marginBottom: '10px' }}>
                        <div>
                            <label style={{ fontSize: '12px', fontWeight: 'bold', display: 'block', marginBottom: '4px' }}>
                                ID <span style={{ color: '#888', fontWeight: 'normal' }}>(skapas automatiskt)</span>
                            </label>
                            <input style={{ ...inputStyle, background: '#e9ecef', color: '#555', cursor: 'not-allowed' }} readOnly
                                value={form.id} />
                        </div>
                        <div>
                            <label style={{ fontSize: '12px', fontWeight: 'bold', display: 'block', marginBottom: '4px' }}>Namn</label>
                            <input style={inputStyle} placeholder="Huvudgateway"
                                value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
                        </div>
                        <div>
                            <label style={{ fontSize: '12px', fontWeight: 'bold', display: 'block', marginBottom: '4px' }}>IP-adress</label>
                            <input style={inputStyle} placeholder="192.168.5.158"
                                value={form.ip} onChange={e => setForm(f => ({ ...f, ip: e.target.value }))} />
                        </div>
                        <div>
                            <label style={{ fontSize: '12px', fontWeight: 'bold', display: 'block', marginBottom: '4px' }}>Beskrivning <span style={{ color: '#888', fontWeight: 'normal' }}>(frivilligt)</span></label>
                            <input style={inputStyle} placeholder="Samordnare hus A"
                                value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
                        </div>
                        <div>
                            <label style={{ fontSize: '12px', fontWeight: 'bold', display: 'block', marginBottom: '4px' }}>Skript-URL <span style={{ color: '#888', fontWeight: 'normal' }}>(frivilligt)</span></label>
                            <input style={inputStyle} placeholder="http://10.0.0.x/script/webhook"
                                value={(form as any).scriptUrl} onChange={e => setForm(f => ({ ...f, scriptUrl: e.target.value }))} />
                        </div>
                    </div>
                    {zigbeeSection(form, setForm as any, showZigbeeAdd, setShowZigbeeAdd, form.ip)}
                    <button onClick={handleAdd} disabled={saving}
                        style={{ ...btnStyle('white', '#28a745', '#28a745'), marginTop: '12px', opacity: saving ? 0.6 : 1 }}>
                        {saving ? 'Sparar…' : 'Registrera gateway'}
                    </button>
                </div>
            )}

            {/* Gateway List */}
            {loading ? (
                <p style={{ color: '#888' }}>Hämtar…</p>
            ) : gateways.length === 0 ? (
                <div style={{ padding: '20px', border: '1px dashed #ccc', borderRadius: '6px', textAlign: 'center', color: '#999' }}>
                    Inga gateways är registrerade än. Klicka på <strong>+ Lägg till gateway</strong> för att lägga till en.
                </div>
            ) : (
                <div>
                    {gateways.map((gw, i) => (
                        <div key={gw.id} style={{ marginBottom: '12px', border: '1px solid #dee2e6', borderRadius: '6px', overflow: 'hidden' }}>
                            {/* Row header */}
                            <div style={{ background: i % 2 === 0 ? '#fff' : '#f8f9fa', padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                                    <span style={{ fontFamily: 'monospace', color: '#555', fontSize: '13px' }}>{gw.id}</span>
                                    <span style={{ fontWeight: 'bold', fontSize: '15px' }}>{gw.name}</span>
                                    <span style={{ fontFamily: 'monospace', color: '#333', fontSize: '12px' }}>{gw.ip}</span>
                                    {gw.description && <span style={{ color: '#666', fontSize: '12px' }}>{gw.description}</span>}
                                    {coordinatorBadge(gw)}
                                    {statusDot(gw)}
                                </div>
                                <div style={{ display: 'flex', gap: '6px' }}>
                                    {gw.zigbeeEnabled && (
                                        <a href={`${API_ORIGIN}/api/gateways/${gw.id}/backup`} target="_blank" rel="noreferrer"
                                            style={{ ...btnStyle('#004085', '#e8f0fe', '#b8daff'), textDecoration: 'none', fontSize: '12px' }}>
                                            ⬇ Säkerhetskopia
                                        </a>
                                    )}                                    {gw.zigbeeEnabled && (
                                        <button
                                            onClick={async () => {
                                                try {
                                                    const blob = await downloadZigbeeDb(gw.id);
                                                    const url = URL.createObjectURL(blob);
                                                    const a = document.createElement('a');
                                                    a.href = url; a.download = `zigbee-${gw.id}.db`;
                                                    a.click(); URL.revokeObjectURL(url);
                                                } catch (err: any) {
                                                    setError(err.message || 'Nedladdningen misslyckades');
                                                }
                                            }}
                                            style={{ ...btnStyle('#383d41', '#e2e3e5', '#d6d8db'), fontSize: '12px' }}
                                            title="Ladda ner zigbee.db">
                                            ⬇ zigbee.db
                                        </button>
                                    )}
                                    {gw.zigbeeEnabled && (
                                        <label
                                            style={{ ...btnStyle('#383d41', '#e2e3e5', '#d6d8db'), fontSize: '12px', cursor: 'pointer', margin: 0 }}
                                            title="Byt ut zigbee.db och starta om samordnaren">
                                            {uploadingDb.has(gw.id) ? 'Laddar upp…' : '⬆ zigbee.db'}
                                            <input type="file" accept=".db" style={{ display: 'none' }}
                                                onChange={async e => {
                                                    const file = e.target.files?.[0];
                                                    if (!file) return;
                                                    e.target.value = '';
                                                    if (!confirmAction(`Byta ut zigbee.db för "${gw.name}"? Samordnaren startar om automatiskt.`)) return;
                                                    setUploadingDb(s => new Set(s).add(gw.id));
                                                    try {
                                                        // 401 hanteras av den gemensamma axios-fångaren (utloggning)
                                                        const result = await uploadZigbeeDb(gw.id, file);
                                                        alert(result.message);
                                                    } catch (err: any) {
                                                        setError(err.message || 'Uppladdningen misslyckades');
                                                    } finally {
                                                        setUploadingDb(s => { const n = new Set(s); n.delete(gw.id); return n; });
                                                    }
                                                }} />
                                        </label>
                                    )}                                    <button onClick={() => startEdit(gw)}
                                        style={{ ...btnStyle('#004085', '#cce5ff', '#b8daff') }}>Ändra</button>
                                    <button onClick={() => handleDelete(gw.id, gw.name)}
                                        style={btnStyle('#721c24', '#f8d7da', '#f5c6cb')}>Ta bort</button>
                                </div>
                            </div>
                            {/* Inline Edit */}
                            {editingId === gw.id && (
                                <div style={{ padding: '16px', background: '#f0f4ff', borderTop: '1px solid #dee2e6' }}>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px', marginBottom: '10px' }}>
                                        {[
                                            { label: 'Namn', key: 'name', placeholder: 'Namn på gatewayen' },
                                            { label: 'IP-adress', key: 'ip', placeholder: '192.168.x.x' },
                                                            { label: 'Beskrivning', key: 'description', placeholder: 'Frivilligt' },
                                            { label: 'Skript-URL', key: 'scriptUrl', placeholder: 'http://10.0.0.x/script/webhook' },
                                        ].map(({ label, key, placeholder }) => (
                                            <div key={key}>
                                                <label style={{ fontSize: '12px', fontWeight: 'bold', display: 'block', marginBottom: '4px' }}>{label}</label>
                                                <input style={inputStyle} placeholder={placeholder}
                                                    value={(editForm as any)[key]}
                                                    onChange={e => setEditForm(f => ({ ...f, [key]: e.target.value }))} />
                                            </div>
                                        ))}
                                    </div>
                                    {zigbeeSection(editForm, setEditForm as any, showZigbeeEdit, setShowZigbeeEdit, editForm.ip)}
                                    <div style={{ marginTop: '12px', display: 'flex', gap: '8px' }}>
                                        <button onClick={() => handleEditSave(gw.id)} disabled={saving}
                                            style={{ ...btnStyle('white', '#28a745', '#28a745'), opacity: saving ? 0.6 : 1 }}>
                                            {saving ? '…' : 'Spara'}
                                        </button>
                                        <button onClick={() => { setEditingId(null); setShowZigbeeEdit(false); }}
                                            style={btnStyle('#555', '#e9ecef', '#ccc')}>Avbryt</button>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Script hint */}
            <div style={{ marginTop: '24px', background: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '6px', padding: '14px 18px', maxWidth: '680px', fontSize: '12px', color: '#555' }}>
                <strong>📋 Så här lägger du in Berry-skriptet</strong><br />
                Ladda upp <code>gateway_monitor.be</code> till varje SLZB-enhet via <em>Settings &gt; Scripts &gt; Upload</em>.<br />
                Sätt <code>GATEWAY_ID</code> till gatewayens ID och <code>GATEWAY_IP</code> till dess IP i det lokala nätet.<br />
                Lägg till det som ska bevakas i listan <code>TARGETS</code> — allt som har en HTTP-port fungerar.
            </div>

            {/* OTA Firmware Updates */}
            <OtaSection gateways={gateways} />

            <ApiKeysSection gateways={gateways} onError={setError} />
        </div>
    );
};

export default Gateways;
